import { airflow } from '@aviata/platform-client';

/** Minimal product shape: only `data.offer.$meta.search_query` is read. */
export type OrderProductWithOfferSearchQuery = {
  data?: {
    offer?: {
      $meta?: {
        search_query?: string | null;
      };
    };
  };
};

function readOfferSearchQuery(product: unknown): string | null {
  const orderProduct = product as OrderProductWithOfferSearchQuery;
  const searchQuery = orderProduct?.data?.offer?.$meta?.search_query;
  return searchQuery ?? null;
}

/**
 * Builds a single hash-code string suitable for `hashCodeLib.getSearchParams` /
 * `hashCodeLib.isMultiTripSearch`.
 *
 * Search queries live on `airflow.booking` products' offer meta. A multi-city
 * (MT) trip lands on the order in one of two shapes:
 *
 *   1. **One booking** whose `$meta.search_query` already encodes every leg
 *      (the natural MT case: server returns one offer with multiple flights
 *      and one MT search_query).
 *   2. **Multiple bookings**, each carrying its own `search_query` covering
 *      one or more legs (split-bookings — virtual interline / per-leg PNRs).
 *
 * The previous implementation had three failure modes that broke 2-route MT:
 *
 *   - It read only `products[0]`'s query and returned `null` when index 0
 *     happened to be a non-booking product (`airflow.payment`,
 *     `airflow.ancillary`, etc.).
 *   - It collapsed each product to its first itinerary (`itineraries[0]`),
 *     silently dropping the second leg of any 2-route MT stored inside a
 *     single booking when other order products were present.
 *   - It did not dedupe queries across products, so two bookings sharing the
 *     same MT search_query produced a query with the first leg duplicated
 *     (`[A→B, A→B]`) instead of the real legs.
 *
 * The fix walks every product, collects **distinct** search queries (in order
 * of first appearance), and:
 *
 *   - returns the single distinct query verbatim when only one is found
 *     (lossless: handles OW, RT, and natively-stored MT without a parse/build
 *     round-trip);
 *   - otherwise concatenates **all** itineraries from each distinct query and
 *     re-emits a combined query, taking passengers / cabinClass from the
 *     first source query.
 *
 * `products` is typed loosely (`unknown`) so callers can pass platform order
 * `Entity[]` unions without narrowing.
 */
export function buildCombinedSearchQueryFromOrderProducts(
  products: readonly unknown[] | null | undefined,
): string | null {
  if (!products?.length) {
    return null;
  }

  const seenQueries = new Set<string>();
  const distinctQueries: string[] = [];
  for (const product of products) {
    const query = readOfferSearchQuery(product);
    if (!query || seenQueries.has(query)) {
      continue;
    }
    seenQueries.add(query);
    distinctQueries.push(query);
  }

  if (distinctQueries.length === 0) {
    return null;
  }
  if (distinctQueries.length === 1) {
    return distinctQueries[0];
  }

  const firstQuery = distinctQueries[0];

  try {
    const firstParams = airflow.utils.parseQuery(firstQuery);
    if (!firstParams) {
      return firstQuery;
    }

    const itineraries = distinctQueries.flatMap(
      query => airflow.utils.parseQuery(query)?.itineraries ?? [],
    );
    if (itineraries.length === 0) {
      return firstQuery;
    }

    return airflow.utils.buildQuery({
      passengers: firstParams.passengers,
      cabinClass: firstParams.cabinClass,
      itineraries,
    });
  } catch {
    return firstQuery;
  }
}
